import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from './UserContext';
import signalService from '../services/signalService';
import storyService from '../services/storyService';
import trimVideo from '../utils/trimVideo';

/**
 * Your Portal going up, after you've already walked away from the composer.
 * Handoff: docs/plans/2026-09-portals-stories/handoff-composer/README.md "Upload behaviour".
 *
 * The rule the whole thing hangs on: SEND returns you to the wall immediately and the file goes
 * up behind you. Nobody else sees the Portal until it is encoded — the server holds it back
 * (Event.SignalMediaPending) — so the only person who needs to know anything is the sender, and
 * only they see a percentage, a spinner or a failure.
 *
 * That is why this lives in a context and not in the composer: the upload has to survive the
 * page that started it. It drives the sender's own rail cell, their navbar avatar, their
 * profile tag and the toast, and it keeps the local copy alive so they can watch their own
 * Portal from their own device while it is still going up.
 */
const PortalPostingContext = createContext({
  posting: null,
  startPosting: async () => {},
  retry: () => {},
  dismiss: () => {},
});

const READY_POLL_MS = 3000;
const TOAST_LINGER_MS = 4000;

export function PortalPostingProvider({ children }) {
  // Mounted inside UserProvider (App.js), so this is a plain read, not a guarded one. The
  // sender's face travels with the posting state to wherever it has to be drawn.
  const { profile } = useUser();
  // { status: 'uploading'|'finishing'|'up'|'failed', progress, localUrl, eventId, storyId, draft }
  const [posting, setPosting] = useState(null);
  const pollRef = useRef(null);
  const dismissRef = useRef(null);
  const draftRef = useRef(null);   // the payload, kept so RETRY can pick up where it stopped

  const clearTimers = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (dismissRef.current) clearTimeout(dismissRef.current);
    pollRef.current = null;
    dismissRef.current = null;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Release the object URL only once the Portal is live — until then it is the only copy the
  // sender can watch.
  const finish = useCallback((localUrl) => {
    dismissRef.current = setTimeout(() => {
      setPosting(null);
      if (localUrl) { try { URL.revokeObjectURL(localUrl); } catch { /* already gone */ } }
    }, TOAST_LINGER_MS);
  }, []);

  /** Poll our own story until the encode lands, then the Portal is everyone's. */
  const waitForReady = useCallback((storyId, localUrl) => {
    clearTimers();
    pollRef.current = setInterval(async () => {
      const r = await storyService.refresh(storyId);
      const status = r?.data?.mediaStatus;
      if (status === 'ready') {
        clearTimers();
        setPosting((p) => (p ? { ...p, status: 'up', progress: 100 } : p));
        finish(localUrl);
      } else if (status === 'failed') {
        clearTimers();
        setPosting((p) => (p ? { ...p, status: 'failed' } : p));
      }
    }, READY_POLL_MS);
  }, [clearTimers, finish]);

  /**
   * Everything the composer collected. The Portal row goes first (so it exists and is held
   * back), then the file, then the two are tied together.
   */
  const run = useCallback(async (draft) => {
    draftRef.current = draft;
    const { media, signalRequest, coverMs, coverFocus, trim, frame } = draft;
    const hasMedia = media && media.kind !== 'text' && media.file;

    setPosting({
      status: 'uploading',
      progress: 0,
      localUrl: media?.localUrl || null,
      // Enough to play your own copy in the viewer before the encode lands: what it is, the
      // slice you kept and where you framed it.
      kind: media?.kind || 'text',
      trim: draft.trim || null,
      frame: draft.frame || null,
      coverUrl: draft.coverUrl || null,
      eventId: draft.eventId || null,
      // The listing it was opened from, so that event's card can say PORTAL OPEN straight away.
      sourceEventId: signalRequest?.sourceEventId || null,
      storyId: null,
      avatarUrl: profile?.avatarUrl || profile?.faceUrl || null,
      initial: (profile?.displayName || 'Y')[0],
    });

    // 1. The Portal itself. With media coming, it lands hidden from everyone but the sender.
    let eventId = draft.eventId;
    if (!eventId) {
      const created = await signalService.create({ ...signalRequest, hasPendingMedia: !!hasMedia });
      if (!created.success) {
        setPosting((p) => (p ? { ...p, status: 'failed' } : p));
        return { success: false, error: created.error };
      }
      eventId = created.data?.eventId;
      draftRef.current = { ...draftRef.current, eventId };
      setPosting((p) => (p ? { ...p, eventId } : p));
    }

    // Text-only is finished the moment the row exists.
    if (!hasMedia) {
      setPosting((p) => (p ? { ...p, status: 'up', progress: 100 } : p));
      finish(null);
      return { success: true, eventId };
    }

    // 2. The clip is cut to its TRIM window on the device first, so only the slice that was
    //    kept goes up (a two-minute 124 MB video leaves as its 30 seconds). If it can't be cut
    //    by copying, the whole file goes and the trim still tells the player what to play.
    //    The cut is kept on the draft, so a RETRY doesn't cut again.
    let sendFile = media.file;
    let sendTrim = trim || null;
    let sendCoverMs = coverMs ?? null;
    if (media.kind === 'video' && trim) {
      const cut = draft.cut !== undefined ? draft.cut : await trimVideo(media.file, trim);
      draftRef.current = { ...draftRef.current, cut };
      if (cut) {
        sendFile = cut.file;
        // The cut starts where the window did: time 0 in the new file is trim.startMs.
        sendTrim = { startMs: 0, endMs: cut.clipMs };
        if (sendCoverMs != null) sendCoverMs = Math.max(0, Math.min(cut.clipMs, sendCoverMs - cut.startMs));
      }
    }

    // 3. The file. This is the first byte that leaves the device. A RETRY after a failed
    //    publish reuses the clip that already went up instead of sending (and paying for) it again.
    let storyId = draft.storyId || null;
    if (!storyId) {
      const upload = media.kind === 'video'
        ? await storyService.uploadVideo(sendFile, {
          onProgress: (pct) => setPosting((p) => (p ? { ...p, progress: Math.min(99, pct) } : p)),
        })
        : await storyService.uploadImage(media.file);

      if (!upload.success) {
        setPosting((p) => (p ? { ...p, status: 'failed' } : p));
        return { success: false, error: upload.error };
      }
      storyId = upload.data?.id;
      draftRef.current = { ...draftRef.current, storyId };
    }
    setPosting((p) => (p ? { ...p, storyId, progress: 100, status: 'finishing' } : p));

    // 4. Tie it to the Portal. A photo is ready here; a clip still has to encode.
    const published = await storyService.publish(storyId, {
      signalEventId: eventId,
      // REPLACE CLIP on the Portal page: this one takes over and the old one is retired.
      replacePortalHero: !!draft.replaceHero,
      audience: signalRequest.audience,
      // Whole numbers: the server stores them that way, and it used to refuse anything else.
      coverMs: sendCoverMs != null ? Math.round(sendCoverMs) : null,
      coverFocus: coverFocus ? { x: Math.round(coverFocus.x), y: Math.round(coverFocus.y) } : null,
      // Where the picture sits in the frame, set by dragging it in the composer.
      frame: frame || null,
      // The slice the sender kept. The whole file goes up; the player loops inside this.
      trimStartMs: sendTrim ? Math.round(sendTrim.startMs) : null,
      trimEndMs: sendTrim ? Math.round(sendTrim.endMs) : null,
      latitude: signalRequest.latitude ?? null,
      longitude: signalRequest.longitude ?? null,
    });
    if (!published.success) {
      setPosting((p) => (p ? { ...p, status: 'failed' } : p));
      return { success: false, error: published.error };
    }

    if (published.data?.mediaStatus === 'ready') {
      setPosting((p) => (p ? { ...p, status: 'up' } : p));
      finish(media.localUrl);
    } else {
      waitForReady(storyId, media.localUrl);
    }
    return { success: true, eventId };
  }, [finish, waitForReady, profile]);

  const startPosting = useCallback((draft) => run(draft), [run]);

  /** The local copy never went anywhere, so a retry resumes rather than restarts. */
  const retry = useCallback(() => {
    if (draftRef.current) run(draftRef.current);
  }, [run]);

  const dismiss = useCallback(() => {
    clearTimers();
    setPosting(null);
  }, [clearTimers]);

  const value = useMemo(() => ({ posting, startPosting, retry, dismiss }), [posting, startPosting, retry, dismiss]);
  return <PortalPostingContext.Provider value={value}>{children}</PortalPostingContext.Provider>;
}

export function usePortalPosting() {
  return useContext(PortalPostingContext);
}

export default PortalPostingContext;
